import { useEffect, useState } from 'react';
import PageHero from '@/components/shared/PageHero';
import { publicApi } from '@/lib/api';
import useSEO from '@/hooks/useSEO';
import RichContent from '@/components/shared/RichContent';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Scale } from 'lucide-react';

export default function CSRGuidelinesMCAPage() {
    const [block, setBlock] = useState(null);
    const [loading, setLoading] = useState(true);

    useSEO({
        title: 'CSR Guidelines (MCA)',
        description:
            'Ministry of Corporate Affairs (MCA) CSR guidelines under Section 135 of the Companies Act, 2013 — applicability, spend obligation, CSR Committee, CSR-1 registration, permissible activities under Schedule VII, reporting and unspent amount.',
    });

    useEffect(() => {
        publicApi
            .getContentBlock('csr.guidelines_mca')
            .then((data) => setBlock(data))
            .catch(() => setBlock(null))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div data-testid="csr-guidelines-mca-page">
            <PageHero
                eyebrow="CSR Projects"
                title={block?.title || 'CSR Guidelines (MCA)'}
                subtitle={
                    block?.subtitle ||
                    'Ministry of Corporate Affairs — statutory framework for Corporate Social Responsibility under Section 135 of the Companies Act, 2013.'
                }
            />

            <section className="section-pad">
                <div className="container-page grid lg:grid-cols-12 gap-8">
                    {/* Illustration */}
                    <aside className="lg:col-span-4">
                        <Card className="bg-card border-border overflow-hidden">
                            <CardContent className="p-0">
                                {block?.image_url ? (
                                    <div className="aspect-[4/3] bg-muted overflow-hidden">
                                        <img
                                            src={block.image_url}
                                            alt="CSR Guidelines"
                                            loading="lazy"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                ) : (
                                    <div className="aspect-[4/3] bg-gradient-to-br from-[hsl(var(--primary))]/10 to-[hsl(var(--accent))]/10 flex items-center justify-center">
                                        <Scale className="h-16 w-16 text-[hsl(var(--primary))]/70" />
                                    </div>
                                )}
                                <div className="p-5">
                                    <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--accent))] mb-2">
                                        Governing Framework
                                    </div>
                                    <div className="font-display font-semibold text-lg leading-snug">
                                        Section 135, Companies Act 2013
                                    </div>
                                    <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                                        Companies (CSR Policy) Rules, 2014 issued by the Ministry of Corporate Affairs.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </aside>

                    {/* Content */}
                    <div className="lg:col-span-8">
                        {loading ? (
                            <div className="space-y-3">
                                <Skeleton className="h-6 w-3/4" />
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-11/12" />
                                <Skeleton className="h-4 w-10/12" />
                            </div>
                        ) : block?.body ? (
                            <RichContent
                                html={block.body}
                                data-testid="csr-guidelines-content"
                                className="text-foreground/85"
                            />
                        ) : (
                            <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                                CSR Guidelines content is being prepared. Please check back shortly.
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
